import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import CheckIcon from '@src/svgs/CheckIcon';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import PlusIcon from '@src/svgs/PlusIcon';
import SparkleIcon from '@src/svgs/SparkleIcon';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import LanguageSamplePlayButton from './LanguageSamplePlayButton';
import VoiceAvatar from './VoiceAvatar';
import { voiceMeta } from './voiceLabels';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

// Styled voice picker (replaces the native <select> in the audition panel).
// Each row shows the voice's Name, Provider and Gender, with the avatar/initials
// — richer than the browser's flat option list. Portaled + dismiss-managed via
// the shared useAnchoredDropdown, matching the app's other anchored dropdowns.
interface VoiceSelectProps {
  voices: AiVoiceRecord[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  id?: string;
  /** IDs of voices activated in the showcase — marked with a workspace check. */
  activatedIds?: Set<string>;
  /**
   * Hover / accessible label for that check. Defaults to "In workspace"; the
   * speaker Manage modal passes "On this speaker", where the set is the
   * speaker's voices rather than the workspace pool.
   */
  activatedLabel?: string;
  /** Trigger text when nothing is selected (defaults to "Select a voice"). */
  placeholder?: string;
  /**
   * `field` (default) renders the card-style select trigger. `button` renders
   * a compact outlined action button (plus glyph + `buttonLabel`) that opens
   * the same menu — for "add one" affordances that should not read as a form
   * field showing a value.
   */
  variant?: 'field' | 'button';
  buttonLabel?: string;
  /**
   * `compact` draws the field trigger on one line — 24px avatar, the name and
   * its badge, no provider/gender line (the open menu still shows it) — for
   * dense lists such as the dub popover's speaker rows. `default` keeps the
   * two-line card trigger used by Manage AI Voices and the audition panel.
   */
  density?: 'default' | 'compact';
  /**
   * Optional non-voice entry rendered at the top of the menu (e.g. "Automatic").
   * Selecting it fires `onChange('')`. `voice` is the voice that entry resolves
   * to today; its avatar fills the row's leading slot so the entry lines up
   * with the voice rows instead of reading as a section heading.
   */
  leadingOption?: { label: string; sublabel?: string; voice?: AiVoiceRecord };
  /** Muted note shown after the selected voice's name in the trigger (e.g. "Automatic"). */
  triggerBadge?: string;
  /**
   * Tooltip for `triggerBadge`, rendered by the caller's react-tooltip
   * instance identified by `tooltipId`. Both must be set for a hint to show.
   */
  triggerBadgeHint?: string;
  tooltipId?: string;
  /**
   * When set (BCP-47 code), each option gets a sample play button so a voice can
   * be auditioned in this language before it's picked (PLAT-10761). Omitted =
   * no per-option preview (the existing management/audition surfaces).
   */
  sampleLanguage?: string;
  /**
   * When set, a "Manage voices →" action renders at the bottom of the open menu
   * — a single contextual entry instead of a link repeated under every row.
   * Fires this callback and closes the menu (PLAT-10761).
   */
  onManageVoices?: () => void;
}

const WorkspaceCheck = ({ label }: { label: string }) => (
  <span className="ws-check" title={label} aria-label={label}>
    <CheckIcon width="11" height="11" fillColor="var(--primary)" />
  </span>
);

const VoiceSelect = ({
  voices,
  value,
  onChange,
  disabled = false,
  id,
  activatedIds,
  activatedLabel = 'In workspace',
  placeholder = 'Select a voice',
  variant = 'field',
  buttonLabel,
  density = 'default',
  leadingOption,
  triggerBadge,
  triggerBadgeHint,
  tooltipId,
  sampleLanguage,
  onManageVoices,
}: VoiceSelectProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // A select-style trigger gets a menu as wide as itself; the compact button
  // trigger needs a real panel width and hangs off its right edge.
  const { menuRef, style } = useAnchoredDropdown(
    open,
    triggerRef,
    () => setOpen(false),
    320,
    variant === 'button' ? { width: 360, align: 'end' } : undefined,
  );

  const selected = voices.find(v => v.id === value) ?? null;
  const compact = density === 'compact';

  const choose = (voiceId: string) => {
    onChange(voiceId);
    setOpen(false);
  };

  return (
    <>
      {variant === 'button' ? (
        <ActionTrigger
          type="button"
          id={id}
          ref={triggerRef}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          <PlusIcon
            width="10"
            height="10"
            strokeColor="currentColor"
            strokeWidth={4}
          />
          {buttonLabel ?? placeholder}
        </ActionTrigger>
      ) : (
        <Trigger
          type="button"
          id={id}
          ref={triggerRef}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          $compact={compact}
        >
          {selected ? (
            <>
              <VoiceAvatar voice={selected} size={compact ? 24 : 32} />
              <span className="labels">
                <span className="name">
                  <span className="name-text">{selected.name}</span>
                  {triggerBadge && (
                    <TriggerBadge
                      data-tooltip-id={triggerBadgeHint ? tooltipId : undefined}
                      data-tooltip-content={triggerBadgeHint}
                    >
                      {triggerBadge}
                    </TriggerBadge>
                  )}
                  {activatedIds?.has(selected.id) && (
                    <WorkspaceCheck label={activatedLabel} />
                  )}
                </span>
                {!compact && (
                  <span className="meta">
                    {voiceMeta(selected.provider, selected.gender)}
                  </span>
                )}
              </span>
            </>
          ) : (
            <span className="labels">
              <span className="name">{placeholder}</span>
            </span>
          )}
          <span className="caret" aria-hidden>
            <ChevronDownIcon width="12" height="12" />
          </span>
        </Trigger>
      )}

      {open &&
        style &&
        createPortal(
          <Menu ref={menuRef} style={style} role="listbox">
            {leadingOption && (
              <>
                {/* A mode, not a voice: no voice tile, a glyph in its place,
                    and its own section, so it cannot read as one more option. */}
                <MenuItem
                  type="button"
                  role="option"
                  aria-selected={value === ''}
                  $selected={value === ''}
                  onClick={() => choose('')}
                >
                  <ModeGlyph aria-hidden>
                    <SparkleIcon
                      width="14"
                      height="14"
                      strokeColor="currentColor"
                    />
                  </ModeGlyph>
                  <span className="labels">
                    <span className="name">{leadingOption.label}</span>
                    {leadingOption.sublabel && (
                      <span className="meta">{leadingOption.sublabel}</span>
                    )}
                  </span>
                </MenuItem>
                <MenuHeading role="presentation">Or pin a voice</MenuHeading>
              </>
            )}
            {voices.map(voice => {
              const label = (
                <>
                  <VoiceAvatar voice={voice} />
                  <span className="labels">
                    <span className="name">
                      <span className="name-text">{voice.name}</span>
                      {activatedIds?.has(voice.id) && (
                        <WorkspaceCheck label={activatedLabel} />
                      )}
                    </span>
                    <span className="meta">
                      {voiceMeta(voice.provider, voice.gender)}
                    </span>
                  </span>
                </>
              );
              // With a sample language, the option is a row: a selectable label
              // button plus a sibling play button (a button can't nest a button).
              if (sampleLanguage) {
                return (
                  <OptionRow key={voice.id} $selected={voice.id === value}>
                    <OptionLabelButton
                      type="button"
                      role="option"
                      aria-selected={voice.id === value}
                      onClick={() => choose(voice.id)}
                    >
                      {label}
                    </OptionLabelButton>
                    {/* The selected voice is already previewable on the trigger
                        row, so skip its duplicate play button here. */}
                    {voice.id !== value && (
                      <LanguageSamplePlayButton
                        aiVoiceId={voice.id}
                        language={sampleLanguage}
                      />
                    )}
                  </OptionRow>
                );
              }
              return (
                <MenuItem
                  key={voice.id}
                  type="button"
                  role="option"
                  aria-selected={voice.id === value}
                  $selected={voice.id === value}
                  onClick={() => choose(voice.id)}
                >
                  {label}
                </MenuItem>
              );
            })}
            {onManageVoices && (
              <ManageVoicesAction
                type="button"
                onClick={() => {
                  setOpen(false);
                  onManageVoices();
                }}
              >
                Manage voices →
              </ManageVoicesAction>
            )}
          </Menu>,
          document.body,
        )}
    </>
  );
};

export default VoiceSelect;

const rowStyles = `
  .labels {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    text-align: left;
  }
  .name {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: var(--foreground);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
  }
  /* Only the name text truncates; the badge / workspace check stay visible. */
  .name-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  .ws-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: color-mix(in srgb, var(--primary) 18%, transparent);
  }
  .meta {
    color: var(--text-muted);
    font-size: var(--text-xs);
  }
`;

// The trigger shows a value, so its name is regular weight: the row label
// beside it (a language, a speaker) is the thing that leads. Options in the
// menu keep the medium weight from rowStyles.
const Trigger = styled.button<{ $compact?: boolean }>`
  .name {
    font-weight: var(--font-weight-normal);
    ${p => p.$compact && 'font-size: var(--text-xs);'}
  }
  display: flex;
  align-items: center;
  gap: ${p => (p.$compact ? '8px' : '12px')};
  width: 100%;
  padding: ${p => (p.$compact ? '4px 10px' : '6px 12px')};
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  background: transparent;
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: var(--font-primary);

  ${rowStyles}

  .labels {
    flex: 1;
  }

  .caret {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    color: var(--text-muted);
  }

  /* Bordered-field pattern (design system → Form Fields): hover escalates to
     --text-muted, the open menu is the field's focus state. Never green. */
  &:hover:not(:disabled) {
    border-color: var(--text-muted);
  }
  &[aria-expanded='true'] {
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// The `button` variant's trigger: the design system's outlined action button
// (same chrome as "Dub with AI Voice" / "Add Translation" in the language menu).
// Compact: it sits beside a section label ("Priority Order"), not in a footer,
// so it is a 28px secondary control, one type step below the label.
export const ActionTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover:not(:disabled),
  &[aria-expanded='true'] {
    background: var(--surface-elevated);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// A status note beside the name (design-system note tag), not a green pill:
// it says how the value was chosen, it is not something to click.
const TriggerBadge = styled.span`
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
`;

// The mode row's glyph slot: same footprint as a voice tile so the labels line
// up, but a quiet sparkle instead of a mic — Automatic is not one of the voices.
const ModeGlyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: var(--surface-elevated);
  color: var(--text-secondary);
`;

// Separates the mode row from the voices it can be swapped for: a hairline and
// a quiet label, so the list reads as "automatic, or one of these".
const MenuHeading = styled.span`
  display: block;
  margin: 4px 0 0;
  padding: 8px 10px 2px;
  border-top: 1px solid var(--border);
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
`;

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.4));
`;

// Footer action at the bottom of the open menu (e.g. "Manage voices →").
// Green marks it as an action distinct from the voice options; separated by
// spacing (no full-width divider, per the design system).
const ManageVoicesAction = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 2px;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--primary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  text-align: left;
  cursor: pointer;
  transition: background 120ms ease;

  &:hover {
    background: var(--surface-elevated);
  }
`;

// Menu-row rule: hover is the elevated surface; the selected value is told
// apart by gold text, so hovering a row can never be mistaken for the choice.
const MenuItem = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: var(--font-primary);

  ${rowStyles}

  .name {
    color: ${p => (p.$selected ? 'var(--accent)' : 'var(--foreground)')};
  }

  &:hover {
    background: var(--surface-elevated);
  }
`;

// Preview variant (sampleLanguage set): the row carries the selected/hover
// background so the label button and the sample play button read as one option.
const OptionRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 8px;
  border-radius: 6px;
  background: transparent;
  transition: var(--transition-normal);

  .name {
    color: ${p => (p.$selected ? 'var(--accent)' : 'var(--foreground)')};
  }

  &:hover {
    background: var(--surface-elevated);
  }
`;

const OptionLabelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-family: var(--font-primary);

  ${rowStyles}
`;
